import { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { fmtMoney, fmtDuration } from "@/lib/format";
import {
  Plus, Trash, Path, FloppyDisk, GitBranch, ArrowsClockwise, ArrowRight,
  Warning, MapTrifold, CalendarCheck, Receipt, CheckCircle,
} from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import StageRow from "@/components/StageRow";
import MapOverview from "@/components/MapOverview";
import { toast } from "sonner";

const DURATIONS = Array.from({ length: 13 }, (_, i) => i * 15);
const newStage = (type = "Collection") => ({ type: type.toLowerCase(), contact_id: null, name: "", address: "", lat: null, lng: null, service_minutes: 30 });
const depotStage = () => ({ type: "depot", contact_id: null, name: "CargoDash Depot", address: "Dawn Park, Tugela St, Pomona AH, Kempton Park, 1619, South Africa", lat: -26.0877177, lng: 28.2644997, service_minutes: 0 });
const newLeg = () => ({ distance_km: "0", drive_minutes: 0, loaded: true, source: "manual", override_reason: "" });

const toDate = (d) => { const o = d.getTimezoneOffset(); return new Date(d.getTime() - o * 60000).toISOString().slice(0, 10); };
const toTime = (d) => { const o = d.getTimezoneOffset(); return new Date(d.getTime() - o * 60000).toISOString().slice(11, 16); };

export default function QuoteCalculator() {
  const { id } = useParams();
  const [sp] = useSearchParams();
  const isAmend = sp.get("amend") === "1";
  const navigate = useNavigate();

  const [settings, setSettings] = useState(null);
  const [vehicles, setVehicles] = useState([]);
  const [clients, setClients] = useState([]);
  const [contacts, setContacts] = useState([]);

  const [title, setTitle] = useState("");
  const [loadRef, setLoadRef] = useState("");
  const [clientId, setClientId] = useState("");
  const [vehicleId, setVehicleId] = useState("");
  const now = new Date(); now.setHours(6, 0, 0, 0);
  const [tripDate, setTripDate] = useState(toDate(now));
  const [startTime, setStartTime] = useState("08:00");
  const [stages, setStages] = useState([depotStage(), newStage("Delivery")]);
  const [legs, setLegs] = useState([newLeg()]);
  const [tolls, setTolls] = useState([]);
  const [tollGates, setTollGates] = useState([]);
  const [tollGateIds, setTollGateIds] = useState([]);
  const [tollReturn, setTollReturn] = useState(false);
  const [otHours, setOtHours] = useState("");
  const [otRate, setOtRate] = useState("");
  const [standingHrs, setStandingHrs] = useState("");
  const [assistantQty, setAssistantQty] = useState("");
  const [tailLift, setTailLift] = useState(false);
  const [tailLiftRate, setTailLiftRate] = useState("");
  const [addlCharge, setAddlCharge] = useState("");
  const [addlDesc, setAddlDesc] = useState("");
  const [halfDay, setHalfDay] = useState(false);
  const [fixedUnits, setFixedUnits] = useState(null);
  const [autoUnits, setAutoUnits] = useState(true);
  const [recovery, setRecovery] = useState(100);
  const [margin, setMargin] = useState("25");
  const [vat, setVat] = useState("15");
  const [dieselOverride, setDieselOverride] = useState("");
  const [priceOverride, setPriceOverride] = useState("");
  const [belowReason, setBelowReason] = useState("");
  const [notes, setNotes] = useState("");
  const [routeCalcAt, setRouteCalcAt] = useState(null);
  const [versionReason, setVersionReason] = useState("");
  const [rootId, setRootId] = useState(null);
  const [mode, setMode] = useState("internal");
  const [supplierId, setSupplierId] = useState("");
  const [hireCost, setHireCost] = useState("");
  const [plainToll, setPlainToll] = useState("");

  const [preview, setPreview] = useState(null);
  const [conflict, setConflict] = useState(null);
  const [showDetail, setShowDetail] = useState(true);
  const [saving, setSaving] = useState(false);
  const debRef = useRef(null);

  useEffect(() => {
    api.get("/settings").then((r) => {
      setSettings(r.data); setMargin(r.data.default_margin_pct); setVat(r.data.default_vat_pct);
      if (!id && Number(r.data.tail_lift_default_rate) > 0) setTailLiftRate(String(r.data.tail_lift_default_rate));
    });
    api.get("/vehicles", { params: { include_inactive: false } }).then((r) => setVehicles(r.data));
    api.get("/contacts").then((r) => { setContacts(r.data); setClients(r.data.filter((c) => c.type === "client")); });
    api.get("/toll-gates").then((r) => setTollGates(r.data));
  }, []);

  useEffect(() => {
    if (!id) return;
    api.get(`/quotes/${id}`).then(({ data }) => {
      setTitle(data.title || ""); setLoadRef(data.load_reference || ""); setClientId(data.client_id || ""); setVehicleId(data.vehicle_id);
      const ts = new Date(data.trip_start); setTripDate(toDate(ts)); setStartTime(toTime(ts));
      setStages(data.stages.map((s) => ({ type: s.type, contact_id: s.contact_id, name: s.name, address: s.address, lat: s.lat, lng: s.lng, service_minutes: s.service_minutes })));
      setLegs(data.legs.map((l) => ({ ...l })));
      setTolls(data.tolls || []); setHalfDay(data.half_day);
      setTollGateIds(data.toll_gate_ids || []); setTollReturn(data.toll_return_trip || false);
      setOtHours(data.overtime_hours || ""); setOtRate(data.overtime_rate || "");
      setStandingHrs(data.standing_hours || ""); setAssistantQty(data.assistant_qty || "");
      setTailLift(data.tail_lift || false); setTailLiftRate(data.tail_lift_rate || "");
      setAddlCharge(data.additional_charge || ""); setAddlDesc(data.additional_charge_desc || "");
      setFixedUnits(data.fixed_units); setAutoUnits(false);
      setRecovery(Number(data.fixed_recovery_pct || 100)); setMargin(data.target_margin_pct); setVat(data.vat_pct);
      setPriceOverride(data.price_override || ""); setNotes(data.notes || ""); setRouteCalcAt(data.route_calculated_at);
      setRootId(data.root_id || null);
      setMode(data.quote_mode || "internal");
      setSupplierId(data.supplier_id || ""); setHireCost(data.hire_cost || "");
      setPlainToll(data.quote_mode === "external" ? (data.tolls_manual || "") : "");
    });
  }, [id]);

  useEffect(() => {
    setLegs((prev) => { const need = Math.max(stages.length - 1, 0); const next = prev.slice(0, need); while (next.length < need) next.push(newLeg()); return next; });
  }, [stages.length]);

  const tripStartISO = () => new Date(`${tripDate}T${startTime}:00`).toISOString();

  const buildBody = useCallback(() => ({
    title: title || null, load_reference: loadRef || null,
    client_id: clientId || null, vehicle_id: vehicleId, trip_start: tripStartISO(),
    stages: stages.map((s) => ({ ...s, service_minutes: Number(s.service_minutes) || 0 })),
    legs: legs.map((l) => ({ ...l, distance_km: String(l.distance_km || "0"), drive_minutes: Number(l.drive_minutes) || 0 })),
    tolls: tolls.map((t) => ({ name: t.name || "Toll", amount: String(t.amount || "0") })),
    quote_mode: mode,
    supplier_id: mode === "external" ? (supplierId || null) : null,
    hire_cost: mode === "external" ? (hireCost || "0") : null,
    tolls_manual: mode === "external" ? (plainToll || null) : null,
    toll_gate_ids: mode === "external" ? [] : tollGateIds,
    toll_return_trip: mode === "external" ? false : tollReturn,
    overtime_hours: otHours || null, overtime_rate: otRate || null,
    standing_hours: standingHrs || null, assistant_qty: assistantQty || null,
    tail_lift: tailLift, tail_lift_rate: tailLiftRate || null,
    additional_charge: addlCharge || null, additional_charge_desc: addlDesc || null,
    return_to_depot: stages.some((s) => s.type === "return"), half_day: halfDay,
    fixed_units: autoUnits ? null : Number(fixedUnits),
    target_margin_pct: margin, vat_pct: vat, fixed_recovery_pct: String(recovery),
    diesel_price_override: dieselOverride || null, price_override: priceOverride || null,
    below_cost_reason: belowReason || null, notes: notes || null, route_calculated_at: routeCalcAt,
  }), [title, loadRef, clientId, vehicleId, tripDate, startTime, stages, legs, tolls, tollGateIds, tollReturn, otHours, otRate, standingHrs, assistantQty, tailLift, tailLiftRate, addlCharge, addlDesc, halfDay, autoUnits, fixedUnits, margin, vat, recovery, dieselOverride, priceOverride, belowReason, notes, routeCalcAt, mode, supplierId, hireCost, plainToll]);

  useEffect(() => {
    if (!vehicleId) { setPreview(null); return; }
    clearTimeout(debRef.current);
    debRef.current = setTimeout(async () => {
      try {
        const { data } = await api.post("/quotes/preview", buildBody());
        setPreview(data);
        if (autoUnits && data.fixed_units != null) setFixedUnits(data.fixed_units);
        // live conflict check
        if (data.trip_start && data.trip_end) {
          const cc = await api.post("/quotes/check-conflicts", { vehicle_id: vehicleId, trip_start: data.trip_start, trip_end: data.trip_end, exclude_root: rootId });
          setConflict(cc.data);
        }
      } catch { /* ignore */ }
    }, 400);
    return () => clearTimeout(debRef.current);
  }, [buildBody, vehicleId, autoUnits, rootId]);

  const c = preview?.costing;
  const veh = vehicles.find((v) => v.id === vehicleId);
  const vehicleList = vehicles.filter((v) => (mode === "external" ? v.is_third_party : !v.is_third_party));
  const switchMode = (m) => {
    setMode(m);
    const v = vehicles.find((x) => x.id === vehicleId);
    if (v && (m === "external") !== !!v.is_third_party) setVehicleId("");
  };

  const updStage = (i, patch) => setStages((s) => s.map((st, idx) => idx === i ? { ...st, ...patch } : st));
  const addStage = () => setStages((s) => [...s, newStage("Waypoint")]);
  const removeStage = (i) => stages.length > 2 && setStages((s) => s.filter((_, idx) => idx !== i));
  const moveStage = (i, dir) => { const j = i + dir; if (j < 0 || j >= stages.length) return; setStages((s) => { const n = [...s]; [n[i], n[j]] = [n[j], n[i]]; return n; }); };
  const updLeg = (i, patch) => setLegs((l) => l.map((lg, idx) => idx === i ? { ...lg, ...patch } : lg));

  const calcRoute = async () => {
    const coords = stages.map((s) => (s.lat && s.lng ? { lat: s.lat, lng: s.lng } : null));
    if (coords.some((x) => !x)) { toast.error("Pin every stage (search the address) before auto-routing"); return; }
    const { data } = await api.post("/routing/calculate", { coords });
    if (!data.ok) { toast.warning(data.message || "Routing unavailable — enter km manually"); return; }
    setLegs((prev) => prev.map((lg, i) => data.legs[i] ? { ...lg, distance_km: String(data.legs[i].distance_km), drive_minutes: data.legs[i].drive_minutes, source: data.legs[i].provider || "osrm" } : lg));
    setRouteCalcAt(data.calculated_at);
    const prov = data.legs[0]?.provider === "google" ? "Google Maps" : "OSRM";
    toast.success(`Route auto-filled from ${prov}`);
  };

  const save = async (useRecommended) => {
    if (!vehicleId) return toast.error("Select a vehicle");
    if (c?.warning && !belowReason.trim()) return toast.error("Price is below cost — a reason is required");
    if (isAmend && !versionReason.trim()) return toast.error("An amendment reason is required");
    setSaving(true);
    try {
      const body = buildBody();
      if (useRecommended) body.price_override = null;
      let res;
      if (isAmend) res = await api.post(`/quotes/${id}/amend`, { ...body, version_reason: versionReason });
      else if (id) res = await api.put(`/quotes/${id}`, body);
      else res = await api.post("/quotes", body);
      const q = res.data.quote || res.data;
      toast.success(isAmend ? "Revision created" : id ? "Quote saved" : "Draft created");
      navigate(`/quotes/${q.id}`);
    } catch (e) {
      const d = e.response?.data?.detail;
      if (e.response?.status === 409 && d?.conflicts) toast.error("Trip conflicts with another booking — resolve on the detail page");
      else toast.error(formatApiError(d));
    } finally { setSaving(false); }
  };

  const totalDriveMin = legs.reduce((a, l) => a + (Number(l.drive_minutes) || 0), 0);
  const totalServiceMin = stages.reduce((a, s) => a + (Number(s.service_minutes) || 0), 0);
  const estCompletion = preview?.trip_end ? new Date(preview.trip_end).toLocaleTimeString("en-ZA", { hour: "2-digit", minute: "2-digit", hour12: false, timeZone: "Africa/Johannesburg" }) : "--:--";

  const hasConflict = !!(conflict && (conflict.conflicts?.length || conflict.soft_conflicts?.length));

  return (
    <div className="pb-10">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4 mb-6">
        <div>
          <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-400 mb-1">{isAmend ? "Quote amendment" : id ? "Quote draft" : "New quote"}</div>
          <h1 className="font-heading font-bold text-2xl sm:text-3xl tracking-tight text-slate-900">Build a dedicated trip quote</h1>
          <p className="text-sm text-slate-500 mt-1">Plan the route, check availability, then price with confidence.</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => save(false)} disabled={saving} data-testid="save-draft-btn"><FloppyDisk size={16} className="mr-1.5" />Save draft</Button>
          <Button onClick={() => save(false)} disabled={saving} className="bg-teal-600 hover:bg-teal-700 text-white" data-testid="review-quote-btn">
            {isAmend ? <><GitBranch size={16} className="mr-1.5" />Create revision</> : <>Review quote <ArrowRight size={16} className="ml-1.5" /></>}
          </Button>
        </div>
      </div>

      {/* Internal / External tabs */}
      <div className="flex items-center gap-1 mb-4 border-b border-slate-200" data-testid="quote-mode-tabs">
        {[["internal", "Internal"], ["external", "External · 3rd party"]].map(([m, label]) => (
          <button key={m} onClick={() => switchMode(m)} data-testid={`quote-tab-${m}`}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${mode === m ? "border-teal-600 text-teal-700" : "border-transparent text-slate-500 hover:text-slate-700"}`}>
            {label}
          </button>
        ))}
      </div>

      {/* Top header card */}
      <div className="bg-white border border-slate-200 rounded-lg p-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
        <Field label="Client">
          <select className="cd-input" value={clientId} onChange={(e) => setClientId(e.target.value)} data-testid="q-client">
            <option value="">— none —</option>
            {clients.map((cl) => <option key={cl.id} value={cl.id}>{cl.name}</option>)}
          </select>
        </Field>
        <Field label="Trip date"><input type="date" className="cd-input" value={tripDate} onChange={(e) => setTripDate(e.target.value)} data-testid="q-trip-date" /></Field>
        <Field label="Vehicle">
          <select className="cd-input" value={vehicleId} onChange={(e) => setVehicleId(e.target.value)} data-testid="q-vehicle">
            <option value="">Select vehicle…</option>
            {vehicleList.map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}
          </select>
          {veh && <div className="text-[11px] text-slate-400 mt-1">{veh.payload_kg ? `${Number(veh.payload_kg).toLocaleString("en-ZA")} kg payload` : "—"} · {veh.pallet_capacity || "—"}</div>}
        </Field>
        <Field label="Load reference"><input className="cd-input" placeholder="e.g. CHEP-140628" value={loadRef} onChange={(e) => setLoadRef(e.target.value)} data-testid="q-load-ref" /></Field>
      </div>

      {/* External supplier + hire row */}
      {mode === "external" && (
        <div className="bg-white border border-slate-200 rounded-lg p-4 grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4" data-testid="external-supplier-row">
          <Field label="3rd party supplier">
            <select className="cd-input" value={supplierId} onChange={(e) => setSupplierId(e.target.value)} data-testid="q-supplier">
              <option value="">— select from contacts —</option>
              {contacts.map((s) => <option key={s.id} value={s.id}>{s.name}{s.type && s.type !== "supplier" ? ` · ${s.type}` : ""}</option>)}
            </select>
          </Field>
          <Field label="Hire cost from supplier (excl VAT)">
            <input type="number" min="0" step="0.01" className="cd-input data-num" placeholder="0.00" value={hireCost} onChange={(e) => setHireCost(e.target.value)} data-testid="q-hire-cost" />
          </Field>
        </div>
      )}

      {/* Conflict banner */}
      {hasConflict && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-4 flex items-start justify-between gap-4" data-testid="conflict-banner">
          <div className="flex items-start gap-3">
            <Warning size={22} weight="fill" className="text-amber-500 mt-0.5 shrink-0" />
            <div>
              <div className="font-medium text-amber-900">{conflict.conflicts?.length ? "Vehicle already allocated on this date" : "Turnaround warning"}</div>
              <div className="text-sm text-amber-800">
                {(conflict.conflicts?.length ? conflict.conflicts : conflict.soft_conflicts).slice(0, 2).map((cf) => (
                  <div key={cf.id}>{veh?.name} — {cf.quote_number} · {new Date(cf.start).toLocaleString("en-ZA", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })} → {new Date(cf.end).toLocaleTimeString("en-ZA", { hour: "2-digit", minute: "2-digit" })}{cf.gap_minutes != null ? ` (${cf.gap_minutes} min gap)` : ""}</div>
                ))}
                <span className="text-xs">Choose another vehicle or review the calendar. You can still override on Accept with a recorded reason.</span>
              </div>
            </div>
          </div>
          <Button variant="outline" size="sm" onClick={() => navigate("/calendar")} className="border-amber-300 text-amber-800 shrink-0" data-testid="view-calendar-btn"><CalendarCheck size={16} className="mr-1.5" />View calendar</Button>
        </div>
      )}

      {/* Main two columns */}
      <div className="grid grid-cols-1 xl:grid-cols-[1fr_420px] gap-4">
        {/* Trip route */}
        <div className="bg-white border border-slate-200 rounded-lg">
          <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between sticky top-16 z-10 bg-white rounded-t-lg">
            <div className="flex items-center gap-2"><Path size={18} className="text-slate-400" weight="bold" /><h3 className="font-heading font-semibold text-slate-900">Trip route</h3></div>
            <div className="flex items-center gap-2">
              <Button size="sm" variant="outline" onClick={calcRoute} data-testid="calc-route-btn"><ArrowsClockwise size={15} className="mr-1.5" />Auto-route</Button>
              <Button size="sm" variant="outline" onClick={addStage} data-testid="add-stage-btn"><Plus size={15} className="mr-1.5" />Add stage</Button>
            </div>
          </div>
          <div className="p-4 space-y-3">
            <p className="text-[11px] text-slate-400">Arrival and departure times recalculate automatically. Manual km & drive-time entry is always available.</p>
            {stages.map((s, i) => (
              <StageRow key={i} index={i} stage={s} leg={i > 0 ? legs[i - 1] : null}
                schedule={preview?.stages?.[i]} isFirst={i === 0} startTime={startTime} onStartTime={setStartTime}
                contacts={contacts} durations={DURATIONS} canRemove={stages.length > 2}
                onType={(v) => updStage(i, { type: v })} onChange={(patch) => updStage(i, patch)}
                onLegChange={(patch) => updLeg(i - 1, patch)} onMove={(dir) => moveStage(i, dir)} onRemove={() => removeStage(i)} />
            ))}
            <Button variant="outline" size="sm" onClick={addStage} data-testid="add-stage-btn-bottom" className="w-full border-dashed text-slate-600"><Plus size={15} className="mr-1.5" />Add stage</Button>
            {/* totals */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 pt-2">
              <Totals label="Total distance" value={`${c?.total_km ?? "0"} km`} />
              <Totals label="Drive time" value={fmtDuration(totalDriveMin)} />
              <Totals label="Onsite / service" value={fmtDuration(totalServiceMin)} />
              <Totals label="Est. completion" value={estCompletion} highlight />
            </div>
          </div>
        </div>

        {/* Route overview + tolls */}
        <div className="space-y-4">
          <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
            <div className="px-5 py-4 border-b border-slate-100 flex items-center gap-2"><MapTrifold size={18} className="text-slate-400" weight="bold" /><h3 className="font-heading font-semibold text-slate-900">Route overview</h3></div>
            <MapOverview stages={stages} />
            <div className="p-4">
              <div className="flex items-center gap-2 mb-2">
                <Receipt size={16} className="text-slate-400" />
                <span className="text-sm font-medium text-slate-700">Tolls</span>
              </div>
              {mode === "external" ? (
                <div data-testid="external-toll">
                  <Field label="Toll / route charges (R)"><input type="number" min="0" step="0.01" className="cd-input data-num" placeholder="0.00" value={plainToll} onChange={(e) => setPlainToll(e.target.value)} data-testid="q-plain-toll" /></Field>
                  <div className="text-right text-sm text-slate-600 mt-2">Toll total <span className="data-num font-semibold text-slate-900">{fmtMoney(c?.tolls_total)}</span></div>
                </div>
              ) : (<>
              {tollGates.length > 0 && (
                <div className="mb-3 border border-slate-200 rounded-md p-3" data-testid="toll-gate-picker">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">Toll gates{veh ? ` · Class ${veh.toll_class || "1"}` : ""}</span>
                    <label className="flex items-center gap-1.5 text-xs text-slate-600"><input type="checkbox" className="accent-teal-600" checked={tollReturn} onChange={(e) => setTollReturn(e.target.checked)} data-testid="toll-return" /> Return trip (×2)</label>
                  </div>
                  {!veh ? <div className="text-xs text-slate-400">Select a vehicle to see applicable toll rates.</div> : (
                    <div className="space-y-0.5 max-h-44 overflow-y-auto cd-scroll">
                      {tollGates.map((g) => {
                        const rate = g[`rate_class_${veh.toll_class || "1"}`] || "0";
                        const checked = tollGateIds.includes(g.id);
                        return (
                          <label key={g.id} className="flex items-center justify-between gap-2 text-sm py-1 cursor-pointer" data-testid={`toll-gate-${g.id}`}>
                            <span className="flex items-center gap-2 text-slate-700"><input type="checkbox" className="accent-teal-600" checked={checked} onChange={() => setTollGateIds(checked ? tollGateIds.filter((x) => x !== g.id) : [...tollGateIds, g.id])} /> {g.name}</span>
                            <span className="data-num text-slate-500">{fmtMoney(rate)}{tollReturn ? " ×2" : ""}</span>
                          </label>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
              {tollGates.length === 0 ? (
                <div className="text-xs text-slate-400 bg-slate-50 rounded-md px-3 py-2">No toll gates configured. Admins can add gates in Settings.</div>
              ) : (
                <div className="text-right text-sm text-slate-600">Toll total <span className="data-num font-semibold text-slate-900">{fmtMoney(c?.tolls_total)}</span></div>
              )}
              </>)}
            </div>
          </div>
        </div>
      </div>

      {/* Commercial pricing */}
      <div className="bg-white border border-slate-200 rounded-lg mt-4">
        <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
          <div>
            <h3 className="font-heading font-semibold text-slate-900">Commercial pricing</h3>
            <p className="text-xs text-slate-500">Set cost recovery and target margin for this trip.</p>
          </div>
          <button className="text-sm text-slate-500 hover:text-slate-900" onClick={() => setShowDetail(!showDetail)} data-testid="toggle-detail">{showDetail ? "Hide" : "Show"} cost detail</button>
        </div>

        {!c ? <div className="p-8 text-center text-sm text-slate-400">Select a vehicle to compute pricing.</div> : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 p-5">
            {/* Sliders */}
            <div className="space-y-6">
              {mode !== "external" && (
              <div>
                <div className="flex items-center justify-between mb-1"><label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Fixed-cost recovery</label><span className="data-num font-semibold text-slate-900">{recovery}%</span></div>
                <input type="range" min="0" max="100" step="5" value={recovery} onChange={(e) => setRecovery(Number(e.target.value))} className="w-full accent-teal-600" data-testid="recovery-slider" />
                <div className="flex justify-between text-[11px] text-slate-400"><span>Idle vehicle</span><span>Full recovery</span></div>
              </div>
              )}
              <div>
                <div className="flex items-center justify-between mb-1"><label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Target gross margin</label><span className="data-num font-semibold text-slate-900">{margin}%</span></div>
                <input type="range" min="0" max="60" step="1" value={Number(margin)} onChange={(e) => { setMargin(String(e.target.value)); setPriceOverride(""); }} className="w-full accent-teal-600" data-testid="margin-slider" />
                <div className="flex justify-between text-[11px] text-slate-400"><span>Break-even</span><span>Premium</span></div>
              </div>
              {mode !== "external" && (
              <div className="grid grid-cols-2 gap-3">
                <Field label="Fixed daily units">
                  <div className="flex items-center gap-2">
                    <label className="flex items-center gap-1 text-xs text-slate-600"><input type="checkbox" className="accent-teal-600" checked={autoUnits} onChange={(e) => setAutoUnits(e.target.checked)} data-testid="auto-units" /> auto</label>
                    <input className="cd-input data-num w-16" disabled={autoUnits} value={fixedUnits ?? 1} onChange={(e) => setFixedUnits(Number(e.target.value))} data-testid="fixed-units" />
                  </div>
                </Field>
                <Field label="Half-day dedicated"><label className="flex items-center gap-2 text-xs text-slate-700 mt-2"><input type="checkbox" className="accent-teal-600" checked={halfDay} onChange={(e) => setHalfDay(e.target.checked)} data-testid="half-day" /> 50% shown / 75% charged</label></Field>
              </div>
              )}
              <div className="grid grid-cols-2 gap-3">
                <Field label="Driver overtime (hrs)"><input type="number" min="0" step="0.5" className="cd-input data-num" placeholder="0" value={otHours} onChange={(e) => setOtHours(e.target.value)} data-testid="ot-hours" /></Field>
                <Field label="Overtime rate (R/hr)"><input type="number" min="0" step="0.01" className="cd-input data-num" placeholder="0.00" value={otRate} onChange={(e) => setOtRate(e.target.value)} data-testid="ot-rate" /></Field>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Standing time (hrs)"><input type="number" min="0" step="0.5" className="cd-input data-num" placeholder="0" value={standingHrs} onChange={(e) => setStandingHrs(e.target.value)} data-testid="standing-hrs" /><span className="text-[11px] text-slate-400">@ {fmtMoney(settings?.standing_rate_per_hour)}/hr</span></Field>
                <Field label="Assistants (qty)"><input type="number" min="0" step="1" className="cd-input data-num" placeholder="0" value={assistantQty} onChange={(e) => setAssistantQty(e.target.value)} data-testid="assistant-qty" /><span className="text-[11px] text-slate-400">@ {fmtMoney(settings?.assistant_rate)} each</span></Field>
              </div>
              <div className="grid grid-cols-2 gap-3 items-start">
                <div>
                  <label className="flex items-center gap-2 text-xs font-semibold text-slate-600"><input type="checkbox" className="accent-teal-600" checked={tailLift} onChange={(e) => setTailLift(e.target.checked)} data-testid="tail-lift-toggle" /> Tail lift charge</label>
                  <input type="number" min="0" step="0.01" className="cd-input data-num mt-1" placeholder="0.00" disabled={!tailLift} value={tailLiftRate} onChange={(e) => setTailLiftRate(e.target.value)} data-testid="tail-lift-rate" />
                </div>
                <Field label="Additional charge">
                  {settings?.additional_charge_presets?.length > 0 && (
                    <div className="flex flex-wrap gap-1 mb-1">
                      {settings.additional_charge_presets.map((p, i) => (
                        <button key={i} type="button" onClick={() => { setAddlCharge(String(p.amount)); setAddlDesc(p.label); }} className="text-[10px] px-2 py-0.5 rounded-full bg-slate-100 hover:bg-teal-100 hover:text-teal-800 text-slate-600 transition-colors" data-testid={`addl-preset-${i}`}>{p.label}</button>
                      ))}
                    </div>
                  )}
                  <input type="number" min="0" step="0.01" className="cd-input data-num" placeholder="0.00" value={addlCharge} onChange={(e) => setAddlCharge(e.target.value)} data-testid="additional-charge" />
                  <input className="cd-input mt-1" placeholder="Description" value={addlDesc} onChange={(e) => setAddlDesc(e.target.value)} data-testid="additional-charge-desc" />
                </Field>
              </div>
              {(c.warning || Number(recovery) < 100) && (
                <div className={`rounded-md p-3 text-sm border-l-4 ${c.warning === "red" ? "bg-red-50 border-red-500 text-red-800" : "bg-amber-50 border-amber-500 text-amber-800"}`} data-testid="floor-box">
                  <div className="font-medium">Variable-cost floor protected</div>
                  <div className="text-xs">Any quote below {fmtMoney(c.variable_floor)} requires approval (recorded reason).</div>
                </div>
              )}
              {c.warning && (
                <div><label className="text-xs font-semibold uppercase tracking-wider text-red-600">Below-cost reason (required)</label><textarea className="cd-input mt-1" rows={2} value={belowReason} onChange={(e) => setBelowReason(e.target.value)} data-testid="below-reason" /></div>
              )}
              {isAmend && (
                <div><label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Amendment reason (required)</label><textarea className="cd-input mt-1" rows={2} value={versionReason} onChange={(e) => setVersionReason(e.target.value)} data-testid="version-reason" /></div>
              )}
            </div>

            {/* Cost detail */}
            {showDetail && (
              <div className="space-y-1">
                {mode === "external"
                  ? <CostRow label="3rd party hire" value={fmtMoney(c.hire_cost)} />
                  : (<>
                    <CostRow label="Variable / km" value={fmtMoney(c.variable_km_cost)} />
                    <CostRow label="Fuel" value={fmtMoney(c.fuel_cost)} />
                  </>)}
                <CostRow label="Tolls & route charges" value={fmtMoney(c.tolls_total)} />
                <CostRow label="Driver overtime" value={fmtMoney(c.overtime_amount)} />
                <CostRow label="Standing time" value={fmtMoney(c.standing_amount)} />
                <CostRow label="Assistants" value={fmtMoney(c.assistant_amount)} />
                <CostRow label="Tail lift" value={fmtMoney(c.tail_lift_amount)} />
                <CostRow label={addlDesc ? `Additional — ${addlDesc}` : "Additional charge"} value={fmtMoney(c.additional_amount)} />
                <CostRow label="Variable trip cost" value={fmtMoney(c.variable_floor)} bold />
                {mode !== "external" && (<>
                <div className="border-t border-dashed border-slate-200 my-1" />
                <CostRow label={`Fixed-cost allocation (${c.fixed_recovery_pct}%)`} value={fmtMoney(c.fixed_total)} />
                <CostRow label={`Daily unit × ${c.fixed_units_display}`} value={fmtMoney(c.daily_fixed_unit)} muted />
                </>)}
                <div className="border-t border-slate-200 my-1" />
                <CostRow label="Total calculated cost" value={fmtMoney(c.quoted_cost_base)} bold />
                <div className="mt-2 text-[11px] text-slate-400">Break-even {fmtMoney(c.break_even)} · Variable floor {fmtMoney(c.variable_floor)}</div>
                <div className="pt-3">
                  <Field label="Manual price override (excl VAT)"><input className="cd-input data-num" placeholder="auto from margin" value={priceOverride} onChange={(e) => setPriceOverride(e.target.value)} data-testid="q-price-override" /></Field>
                </div>
              </div>
            )}

            {/* Recommended price card */}
            <div className="bg-slate-900 rounded-lg p-5 text-white h-max">
              <div className="flex items-center justify-between mb-1">
                <span className="text-[11px] uppercase tracking-[0.18em] text-slate-400">Recommended price</span>
                <span className="text-[11px] uppercase tracking-wider text-slate-400">excl. VAT</span>
              </div>
              <div className="font-heading font-bold text-3xl data-num" data-testid="recommended-price">{fmtMoney(c.selling_ex_vat)}</div>
              <div className="mt-2 bg-slate-800 rounded-md px-3 py-2 flex items-center justify-between">
                <span className="text-xs text-slate-300">Expected gross profit</span>
                <span className="data-num text-sm font-semibold text-teal-400">{fmtMoney(c.profit)} · {c.margin_pct}%</span>
              </div>
              <div className="grid grid-cols-3 gap-2 mt-3">
                <PriceCell label="Cost base" value={fmtMoney(c.quoted_cost_base)} />
                <PriceCell label="Break-even" value={fmtMoney(c.break_even)} />
                <PriceCell label="Variable floor" value={fmtMoney(c.variable_floor)} />
              </div>
              <Button onClick={() => save(true)} disabled={saving} className="w-full mt-4 bg-teal-500 hover:bg-teal-400 text-slate-900 font-semibold h-11" data-testid="use-recommended-btn">
                <CheckCircle size={18} weight="fill" className="mr-2" />{isAmend ? "Save revision" : "Use recommended price"}
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function Field({ label, children }) { return <div><label className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">{label}</label><div className="mt-1">{children}</div></div>; }
function Totals({ label, value, highlight }) { return <div className={`rounded-md px-3 py-2 border ${highlight ? "bg-teal-50 border-teal-200" : "bg-slate-50 border-slate-100"}`}><div className="text-[10px] uppercase tracking-wider text-slate-400">{label}</div><div className={`data-num font-semibold ${highlight ? "text-teal-700" : "text-slate-900"}`}>{value}</div></div>; }
function CostRow({ label, value, bold, muted }) { return <div className="flex items-center justify-between py-0.5"><span className={`text-sm ${bold ? "font-semibold text-slate-900" : muted ? "text-slate-400 text-xs" : "text-slate-500"}`}>{label}</span><span className={`data-num text-sm ${bold ? "font-semibold text-slate-900" : muted ? "text-slate-400" : "text-slate-700"}`}>{value}</span></div>; }
function PriceCell({ label, value }) { return <div className="bg-slate-800 rounded-md px-2 py-2"><div className="text-[9px] uppercase tracking-wider text-slate-400">{label}</div><div className="data-num text-xs font-semibold text-white">{value}</div></div>; }
